import { h, JSX, Ref } from "preact";

import { cmdKey } from "../../utils/keyboard";
import router from "../router";

type InheritedAnchorProps = Omit<JSX.HTMLAttributes<HTMLAnchorElement>, "href" | "target" | "rel" | "onClick">;

interface Props extends InheritedAnchorProps {
    anchorRef?: Ref<HTMLAnchorElement>;
    to?: string;
    back?: string;
    backURL?: string;
    navOp?: "replace" | "pop" | "push";
    flowNav?: boolean;
    isExternal?: boolean;
    disableSpecialKeys?: boolean;
    onClick?: (ev: MouseEvent) => void;
    onRouterHistoryUpdated?: () => void;
}

function Link({
    anchorRef,
    to = "",
    back,
    backURL,
    navOp = "push",
    flowNav,
    isExternal = false,
    disableSpecialKeys = false,
    onClick,
    onRouterHistoryUpdated,
    ...nativeAnchorProps
}: Props) {
    const isInternalLink = !isExternal && (to === "#" || router.match(to));
    const isMailLink = to.startsWith("mailto:");

    function handleClick(event: MouseEvent) {
        onClick?.(event);

        // This is treated as an external link
        if (!disableSpecialKeys && (cmdKey(event) || event.shiftKey)) {
            // cmdPressed -> open in new tab
            // shiftPressed -> open in new window
            return;
        }

        if (!isInternalLink || isMailLink) {
            return;
        }

        event.preventDefault();

        if (to === window.location.href || to === "") {
            return;
        }

        const shouldPreventNavigation = router.shouldPreventNavigation(to, event);
        if (shouldPreventNavigation) {
            return;
        }

        const options: { flowNavigation?: boolean; } = {};
        if (flowNav) {
            options.flowNavigation = true;
        }

        const currentURL = location.href;

        switch (navOp) {
            case "replace":
                router.history.replace(to, options);
                break;

            case "pop":
                router.history.pop();
                break;

            case "push":
            default:
                router.history.push(to, options);
        }

        onRouterHistoryUpdated?.();

        if (typeof back !== "undefined") {
            if (back && router.onPushToBackStack) {
                router.onPushToBackStack(back, backURL || currentURL);
            }
        }
    }

    const href = to === "" ? undefined : to;
    const target = isExternal ? "_blank" : undefined;
    const rel = isExternal ? "noopener noreferrer" : undefined;

    return (
        <a
            href={href}
            target={target}
            rel={rel}
            ref={anchorRef}
            onClick={handleClick}
            {...nativeAnchorProps}/>
    );
}

export default Link;
export {
    Props as LinkProps
};
